import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosProgressEvent } from 'axios';
import {
  deleteImport,
  executeImport,
  getImport,
  getImports,
  updateImport,
  uploadImportFile,
} from '../api/import.api';
import { ImportRecord, ImportStatus, UpdateImportRequest } from '../types/Import';
import { queryClient } from '../queryClient';
import { QUERY_CONFIG } from '../constants/queryConfig';

export const useGetImports = (workspaceId: number | null) => {
  return useQuery<ImportRecord[], Error>({
    queryKey: ['imports', workspaceId],
    queryFn: async () => (await getImports(workspaceId!)).data,
    enabled: !!workspaceId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

export const useGetImport = (importId: number | null) => {
  return useQuery<ImportRecord, Error>({
    queryKey: ['import', importId],
    queryFn: async () => (await getImport(importId!)).data,
    enabled: !!importId,
    staleTime: QUERY_CONFIG.STALE_TIME,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === ImportStatus.IN_PROGRESS) {
        return 3000;
      }
      return false;
    },
  });
};

export const useUploadImportFile = () => {
  return useMutation({
    mutationFn: async ({
      workspaceId,
      file,
      onUploadProgress,
    }: {
      workspaceId: number;
      file: File;
      onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
    }) => {
      const response = await uploadImportFile(workspaceId, file, onUploadProgress);
      return response.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['imports', data.workspaceId] });
    },
  });
};

export const useUpdateImport = () => {
  return useMutation({
    mutationFn: async ({ importId, data }: { importId: number; data: UpdateImportRequest }) => {
      const response = await updateImport(importId, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['import', data.id], data);
      void queryClient.invalidateQueries({ queryKey: ['imports', data.workspaceId] });
    },
  });
};

export const useExecuteImport = () => {
  return useMutation({
    mutationFn: async ({ importId }: { importId: number }) => {
      await executeImport(importId);
      return importId;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['import', variables.importId] });
    },
  });
};

export const useDeleteImport = () => {
  return useMutation({
    mutationFn: async ({ importId }: { importId: number }) => {
      await deleteImport(importId);
      return importId;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['imports'] });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['epics'] });
    },
  });
};
